import { clamp01, lerp, easeOutCubic, easeInCubic, easeOutExpo, easeOutBack } from "../core/easing";
import { settle, settleScale } from "./anim";
import type { MotionSig } from "./theme";

/**
 * Anivaze v4 — frame- & theme-aware scene motion.
 *
 * Pure functions of (localTime, …) so the live player and the (future) exporter
 * render identically. Replaces v3's `sceneMotion` which hardcoded an 800×450
 * centre; here the entrance scales with the layout unit and the easing comes
 * from the active theme's motion signature. See PLAN §7.
 */

/** Easing curve for a theme's motion signature. */
export function themeEase(sig: MotionSig): (t: number) => number {
  switch (sig) {
    case "snappy":
      return easeOutBack; // overshoots slightly — punchy social feel
    case "kinetic":
      return easeOutExpo; // fast in, hard settle — for beat-synced type
    default:
      return easeOutCubic; // smooth, calm
  }
}

export interface SceneMotion {
  opacity: number;
  scale: number;
  /** vertical offset in frame px. */
  translateY: number;
}

const ENTER = 0.55;
const EXIT = 0.4;

/**
 * Group-level entrance → hold → exit, scaled to the frame's layout unit.
 * The entrance is a spring SETTLE (anim.ts): content arrives with a small
 * overshoot and rests — slow-out with life, never a linear fade (the
 * "slideshow" tell). Exit stays quick and clean.
 */
export function sceneMotion(localTime: number, duration: number, unit: number, sig: MotionSig): SceneMotion {
  // Snappy/kinetic themes bounce a little harder; calm themes settle gently.
  const bounce = sig === "snappy" ? 0.38 : sig === "kinetic" ? 0.3 : 0.2;
  const enter = settle(localTime, ENTER, bounce);
  const exit = easeInCubic(clamp01((localTime - (duration - EXIT)) / EXIT));
  return {
    opacity: clamp01(enter * 1.15) * (1 - exit),
    scale: settleScale(localTime, ENTER, bounce) * (1 - 0.03 * exit),
    translateY: lerp(unit * 1.4, 0, clamp01(enter)) + unit * exit,
  };
}

/**
 * Per-item staggered reveal progress (0..1). Word-by-word or line-by-line
 * builds read from this so kinetic type feels choreographed, not simultaneous.
 */
export function stagger(
  localTime: number,
  index: number,
  opts: { start?: number; step?: number; dur?: number; sig?: MotionSig } = {},
): number {
  const { start = 0.15, step = 0.12, dur = 0.45, sig = "smooth" } = opts;
  const raw = clamp01((localTime - (start + index * step)) / dur);
  return themeEase(sig)(raw);
}

/** Eased 0..1 progress over a window [start, start+dur]. */
export function ramp(localTime: number, start: number, dur: number, sig: MotionSig = "smooth"): number {
  return themeEase(sig)(clamp01((localTime - start) / dur));
}
